import { useTranslation } from "react-i18next";
import { useContext } from "react";
import { getTranslatedValue, TranslatableEntity, SupportedLanguage } from "../types/translations.types";
import { ModalLanguageContext } from "../context/ModalLanguageContext";

/**
 * Hook to get translated value for a field with automatic language detection
 * Falls back to English if translation doesn't exist
 * 
 * In page builder context, uses ModalLanguageContext for language selection
 * Otherwise, uses browser's i18n language
 */
export function useTranslatedValue<T extends TranslatableEntity>(
  entity: T | null | undefined,
  fieldName: keyof T
): string {
  const { i18n } = useTranslation();
  const modalLanguageContext = useContext(ModalLanguageContext);

  if (!entity) {
    return "";
  }

  // Priority: 1. ModalLanguageContext (for page builder), 2. Browser i18n language
  let language: SupportedLanguage;

  if (modalLanguageContext) {
    // Use modal language context (page builder)
    language = modalLanguageContext.currentLanguage;
  } else {
    // Use browser language (normal pages)
    const currentLang = (i18n.language || "en").split("-")[0] as SupportedLanguage;
    language = ["en", "de", "ar"].includes(currentLang) ? currentLang : "en";
  }

  return getTranslatedValue(entity, fieldName, language);
}

